import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { documentLibraryControllerBulkDeleteDocumentsMutation } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationDocuments } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { BulkDeleteRes } from '@dealstack/shared/lib/generated';
import { BulkDocumentLibraryFiltersDto } from '@dealstack/shared/lib/backendClient';

interface BulkDeleteVariables {
	allDocumentsSelected: boolean;
	excludedDocumentIds?: string[];
	selectedDocumentIds?: string[];
	filters?: BulkDocumentLibraryFiltersDto;
}

/**
 * Bulk-delete documents from the library. The backend performs the soft-delete
 * + link severance for every eligible id in a single transaction; either all
 * eligible docs are deleted or none are (no partial-failure state). Workflow-
 * generated ids are silently skipped server-side as defense in depth.
 */
export const useBulkDeleteDocuments = ({
	dealId,
	onSuccess,
}: {
	dealId: string;
	onSuccess?: (result: BulkDeleteRes) => void;
}) => {
	const queryClient = useQueryClient();
	const tDocuments = useTranslationDocuments();

	const mutation = useMutation({
		...documentLibraryControllerBulkDeleteDocumentsMutation(),
		onSuccess: async (result) => {
			await queryClient.refetchQueries({
				queryKey: [
					formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENTS_GET, { dealId }),
				],
			});

			const successCount = result.deleted.length;
			if (successCount === 1) {
				showToastV2({
					title: tDocuments('bulk_delete.success.singular'),
					state: 'success',
				});
			} else if (successCount > 1) {
				showToastV2({
					title: tDocuments('bulk_delete.success.plural', { count: successCount }),
					state: 'success',
				});
			}
			// successCount === 0 happens only when every submitted id was workflow-generated
			// (FE filter bypassed) or no ids were submitted. Defense-in-depth case; no toast.

			onSuccess?.(result);
		},
		onError: () => {
			showToastV2({
				title: tDocuments('bulk_delete.error.full_failure'),
				state: 'error',
			});
		},
	});

	return {
		bulkDelete: (variables: BulkDeleteVariables) =>
			mutation.mutateAsync({ path: { dealId }, body: variables }),
		isPending: mutation.isPending,
	};
};
